// Team sidebar (was Workforce; renamed by the founder's decision of 2026-10-30, CONNECTIONS-AREA,
// which also adds Connections after Agents). (UX pass 1.1, 2026-10-27.) Digital employees (the DE
// roster) · Agents (the AG list) · Activity (the feed of runs; Runs and Wake-ups are reached from
// it) · Loops (LOOP-BREAKER) · Alerts (managers; MONITORING-P1) · Fleet (managers; MONITORING-P2) · Approvals (+ the caller's pending count, `my/approvals/count`) · Outbox (STEP1-OUTBOX) ·
// Rooms · Templates (managers; TEMPLATES-T2..T4). Then one row per
// DE from `digitalEmployees.list` (the same cached query as the roster, so the list costs no
// extra call), each → `/agents/de/{id}` and the DE in focus, "Load more" while the server has
// more, and a "+" on the caption (managers: Add digital employee); then the AGs, each badged.
// Moved out: My agent (the account menu), Budgets and Skill packs (Setup › Workforce), the "In
// Setup" footer (Setup and ⌘K). With the `agents` add-on off every row stays: its screen is
// read-only under the add-on banner, never hidden (founder's rule).
import {
  AvatarTile,
  Button,
  CountPill,
  NavList,
  NavRow,
  SidebarCaption,
  SidebarEmpty,
  SidebarPanel,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useRouteContext } from "@tanstack/react-router";
import {
  BotIcon,
  ChevronRightIcon,
  ChevronsDownIcon,
  HandIcon,
  InboxIcon,
  LayoutTemplateIcon,
  PlugIcon,
  PlusIcon,
  RadioIcon,
  RepeatIcon,
  SendIcon,
  ServerIcon,
  SirenIcon,
  SparklesIcon,
  UsersRoundIcon,
  WorkflowIcon,
} from "lucide-react";
import { type ComponentProps, useMemo } from "react";
import type { SidebarProps } from "../../extensions/registry";
import { AddAgentButton, mayAddAgent } from "../../features/agents/add-ag-dialog";
import { myApprovalsCountQuery } from "../../features/agents/agent-queries";
import { RequestAgentButton, useAgentMode } from "../../features/agents/agent-requests";
import { useMyMembershipId } from "../../features/agents/agents-ux";
import { AskForHelperButton } from "../../features/agents/helper-requests";
import {
  type Helper,
  HelperDot,
  HelpersToggle,
  helperCounts,
  helperWorking,
  useHelpers,
  useOpenLeads,
} from "../../features/agents/helpers";
import { isManager } from "../../features/company";
import { deLabel } from "../../features/de/de-name";
import { DE_STATUS_LABELS } from "../../features/de/de-status";
import { READINESS_LABELS } from "../../features/de/readiness";
import { deployedUnverified } from "../../features/de/unverified-badge";
import { digitalEmployeesQuery, workforceAgentsQuery } from "../../features/queries";
import { useFeature } from "../../features/use-feature";
import { keepFocus, useFocus } from "../../focus/focus";

/**
 * Approvals with the caller's pending count. AGT-UI-2: the count is its own read,
 * `my/approvals/count` (one number), on every Agents page with the add-on on, recounted when
 * anything invalidates `["company", co, "approvals"]` (a decision, a change ping). It is a shell
 * read like `/v1/me`, not a screen's (acceptance 3 counts a screen's calls; test/render-app.tsx
 * `SHELL_READS`).
 */
function ApprovalsRow({ company }: { company: SidebarProps["company"] }) {
  const { api } = useRouteContext({ from: "__root__" });
  const agents = useFeature("agents", company.id);
  const mine = useQuery({ ...myApprovalsCountQuery(api, company.id), enabled: agents.enabled });
  const count = mine.data?.count ?? 0;
  return (
    <NavRow
      icon={HandIcon}
      label="Approvals"
      trailing={
        count ? (
          <CountPill count={count} tone="accent" aria-label={`${count} waiting for you`} />
        ) : null
      }
      render={
        <Link
          to="/c/$slug/agents/approvals"
          params={{ slug: company.slug }}
          search={keepFocus}
          activeOptions={{ includeSearch: false }}
        />
      }
    />
  );
}

/** One helper (AG) row, opening its own page; its state when not active. */
function AgNavRow({ company, agent: a }: { company: SidebarProps["company"]; agent: Helper }) {
  return (
    <NavRow
      icon={WorkflowIcon}
      label={workerName(a.name)}
      badge={<WorkerBadge kind="ag" size="sm" />}
      trailing={
        a.status !== "active" ? (
          <span className="text-2xs text-muted-foreground">
            {a.status === "paused"
              ? "Paused"
              : a.status === "not_deployed"
                ? "Not deployed"
                : "Retired"}
          </span>
        ) : null
      }
      // WF-FIX: each entry opens its own agent (it opened the list before).
      render={
        <Link
          to="/c/$slug/agents/ag/$id"
          params={{ slug: company.slug, id: a.id }}
          search={keepFocus}
          activeOptions={{ includeSearch: false }}
          title={`Reports to ${workerName(a.reports_to.name)}`}
        />
      }
    />
  );
}

/** HELPERS: a DE's helpers, open under its row — one 13 px line each, opening the helper's page. */
function SidebarHelperList({
  company,
  helpers,
  now,
}: {
  company: SidebarProps["company"];
  helpers: readonly Helper[];
  now: number;
}) {
  return (
    <ul data-slot="sidebar-helpers" className="m-0 mb-1 flex list-none flex-col p-0 pl-9">
      {helpers.map((a) => {
        const name = workerName(a.name);
        const on = helperWorking(a, now);
        return (
          <li key={a.id} data-sidebar-helper={a.id}>
            <Link
              to="/c/$slug/agents/ag/$id"
              params={{ slug: company.slug, id: a.id }}
              search={keepFocus}
              className="mr-2 flex min-h-7 items-center gap-1.5 rounded-md px-2 text-[13px] text-sidebar-foreground hover:bg-muted hover:text-foreground"
              title={`${name} · ${on ? "Working" : a.status === "paused" ? "Paused" : "Idle"}`}
            >
              <WorkerBadge kind="ag" size="sm" />
              <span className="min-w-0 flex-1 truncate">{name}</span>
              <HelperDot working={on} />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export function AgentsSidebar({ company, children }: SidebarProps) {
  const { api } = useRouteContext({ from: "__root__" });
  const agents = useFeature("agents", company.id);
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  // WORKFORCE: the agents (AG), the same cached read as the Agents list and the seat tree.
  const ags = useQuery(workforceAgentsQuery(api, company.id));
  const { byLead, available, now } = useHelpers(api, company.id);
  const meId = useMyMembershipId(company.id);
  const [open, toggle] = useOpenLeads(company.id, meId, "workforce");
  const { focus } = useFocus();
  // Retired DEs stay off the list (the roster's "Show retired" lists them), unless in focus.
  const rows = (des.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (d) =>
      d.status !== "retired" ||
      (focus.subject?.kind === "de" && focus.subject.id === d.membership_id),
  );
  // HELPERS: the deployed helpers not shown under a listed DE (a person's, or a DE not loaded).
  const listed = useMemo(() => new Set(rows.map((d) => d.membership_id)), [rows]);
  const loose = (ags.data ?? []).filter(
    (a) =>
      a.status !== "not_deployed" &&
      !(a.reports_to_kind === "digital_employee" && listed.has(a.reports_to_membership_id)),
  );
  const params = { slug: company.slug };
  const active = { includeSearch: false } as const;
  const manager = isManager(company);
  const mode = useAgentMode(api, company);
  const row = (icon: typeof BotIcon, label: string, to: string, exact = false) => {
    // Every `to` is one of the app's routes; the generic Link props are too wide to name here.
    const link = {
      to,
      params,
      search: keepFocus,
      activeOptions: exact ? { exact: true, includeSearch: false } : active,
    } as unknown as ComponentProps<typeof Link>;
    return <NavRow icon={icon} label={label} render={<Link {...link} />} />;
  };
  return (
    <SidebarPanel title="Team">
      <NavList>
        {row(BotIcon, "Digital employees", "/c/$slug/agents", true)}
        {/* HELPERS: Team › Agents is "Helpers" (one noun; the AG badge stays the type). */}
        {row(WorkflowIcon, "Helpers", "/c/$slug/agents/ag", true)}
        {/* HELPERS: the skill library — each skill once, with who uses it. */}
        {row(SparklesIcon, "Skills", "/c/$slug/agents/skill-library")}
        {/* CONNECTIONS-AREA: everything people and DEs log in to (sign-ins, keys, logins). */}
        {row(PlugIcon, "Connections", "/c/$slug/agents/connections")}
        {/* ACTIVITY: the feed of runs; Runs (a link on Activity) stays until Andrei decides. */}
        {row(RadioIcon, "Activity", "/c/$slug/agents/activity")}
        {/* LOOP-BREAKER: the runs stopped as loops, what they burned; retry, reassign, close. */}
        {row(RepeatIcon, "Loops", "/c/$slug/agents/loops")}
        {/* MONITORING-P1: stuck runs, post storms, stalled jobs, tripped guards (managers). */}
        {manager ? row(SirenIcon, "Alerts", "/c/$slug/agents/alerts") : null}
        {/* MONITORING-P2: where each DE's bridge runs, its version and last word (managers). */}
        {manager ? row(ServerIcon, "Fleet", "/c/$slug/agents/fleet") : null}
        {agents.enabled ? (
          <ApprovalsRow company={company} />
        ) : (
          row(HandIcon, "Approvals", "/c/$slug/agents/approvals")
        )}
        {/* STEP1-OUTBOX: approved outward acts, sent or waiting (retry, cancel). */}
        {row(SendIcon, "Outbox", "/c/$slug/agents/outbox")}
        {/* INC2-INTAKE: what came in and what became of it; dead letters (managers). */}
        {manager ? row(InboxIcon, "Intake", "/c/$slug/agents/intake") : null}
        {row(UsersRoundIcon, "Rooms", "/c/$slug/agents/rooms")}
        {/* TEMPLATES-T2..T4: the role templates and the company's overlay (managers). */}
        {manager ? row(LayoutTemplateIcon, "Templates", "/c/$slug/agents/templates") : null}
      </NavList>
      <SidebarCaption
        label="Digital employees"
        action={
          manager && mode.ready && !mode.canAdd ? (
            // MANAGED-POLICY: a managed company asks its agency for a DE.
            <RequestAgentButton api={api} company={company} kind="de" variant="icon" />
          ) : manager && mode.ready ? (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Add digital employee"
              title="Add digital employee"
              render={<Link to="/c/$slug/setup/des/new" params={params} search={keepFocus} />}
            >
              <PlusIcon aria-hidden="true" />
            </Button>
          ) : null
        }
      />
      <NavList data-slot="sidebar-des">
        {rows.map((d) => {
          const helpers = byLead.get(d.membership_id) ?? [];
          const counts = helperCounts(helpers, now);
          const label = deLabel(d.display_name, d.person_name);
          const isOpen = open.has(d.membership_id) && counts.total > 0;
          return (
            <li key={d.membership_id} className="relative" data-sidebar-de={d.membership_id}>
              <ul className="m-0 list-none p-0">
                <NavRow
                  lead={
                    <AvatarTile
                      name={workerName(d.display_name)}
                      kind="agent"
                      size="xs"
                      aria-hidden="true"
                    />
                  }
                  label={label}
                  badge={<WorkerBadge kind="de" size="sm" />}
                  active={focus.subject?.kind === "de" && focus.subject.id === d.membership_id}
                  trailing={
                    <>
                      {d.status !== "active" ? (
                        <span className="text-2xs text-muted-foreground">
                          {d.status === "onboarding"
                            ? READINESS_LABELS[d.readiness.state]
                            : DE_STATUS_LABELS[d.status]}
                        </span>
                      ) : deployedUnverified(d) ? (
                        // DC-05: a DE running an unverified bundle (a pilot).
                        <span
                          className="text-2xs text-warning-text"
                          data-slot="sidebar-de-unverified"
                        >
                          Unverified
                        </span>
                      ) : null}
                      {/* HELPERS: room for the helper toggle laid over the row's right end. */}
                      {counts.total ? <span aria-hidden="true" className="w-8 shrink-0" /> : null}
                    </>
                  }
                  render={
                    <Link
                      to="/c/$slug/agents/de/$id"
                      params={{ ...params, id: d.membership_id }}
                      search={keepFocus}
                      activeOptions={active}
                    />
                  }
                />
              </ul>
              {/* HELPERS (founder 2026-10-30): each DE folds open to its helpers. */}
              <HelpersToggle
                lead={workerName(label)}
                counts={counts}
                open={isOpen}
                onToggle={() => toggle(d.membership_id)}
                className="absolute top-[18px] right-1 z-10 -translate-y-1/2 pointer-coarse:top-[22px]"
              />
              {isOpen ? <SidebarHelperList company={company} helpers={helpers} now={now} /> : null}
            </li>
          );
        })}
        {des.hasNextPage ? (
          <NavRow
            icon={ChevronsDownIcon}
            label={des.isFetchingNextPage ? "Loading…" : "Load more"}
            render={<button type="button" onClick={() => void des.fetchNextPage()} />}
          />
        ) : null}
      </NavList>
      {des.isSuccess && !rows.length ? (
        <SidebarEmpty
          things="DEs"
          {...(manager && mode.ready
            ? { action: mode.canAdd ? "Add one with +" : "Request one from your agency" }
            : {})}
        />
      ) : des.isError ? (
        <p className="px-4 py-1 text-xs text-muted-foreground">The DE list could not be loaded.</p>
      ) : null}
      {/* WF-POLISH: the caption always shows, with "+" (Add agent) for who may add one. */}
      <SidebarCaption
        label="Helpers"
        action={<AddAgentButton api={api} company={company} variant="icon" />}
      />
      {ags.isSuccess && !ags.data.length ? (
        <SidebarEmpty
          things="helpers"
          {...(mayAddAgent(company) && mode.ready
            ? { action: mode.canAdd ? "Add one with +" : "Request one from your agency" }
            : {})}
        />
      ) : null}
      {/* HELPERS: a DE's helpers sit under the DE above; here the ones a person leads (and any
          whose DE is not listed), each as before. */}
      {loose.length ? (
        <NavList data-slot="sidebar-ags">
          {loose.map((a) => (
            <AgNavRow key={a.id} company={company} agent={a} />
          ))}
        </NavList>
      ) : null}
      {/* HELPERS (founder 2026-10-30): the not-deployed helpers, folded into one section. */}
      {available.length ? (
        <details className="group/avail" data-slot="sidebar-available">
          <summary className="flex min-h-8 cursor-pointer list-none items-center gap-1 px-4 text-xs text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
            <ChevronRightIcon
              aria-hidden="true"
              className="size-3 transition-transform duration-(--dur-fast) group-open/avail:rotate-90"
            />
            Available helpers
            <span className="tabular-nums text-faint">({available.length})</span>
          </summary>
          <NavList>
            {available.map((a) => (
              <AgNavRow key={a.id} company={company} agent={a} />
            ))}
          </NavList>
          <div className="px-4 py-1">
            <AskForHelperButton
              api={api}
              company={company}
              meId={meId}
              des={rows.filter((d) => d.status !== "retired")}
              available={available}
              variant="link"
            />
          </div>
        </details>
      ) : null}
      {children}
    </SidebarPanel>
  );
}
